import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getSupplierPreliminaryNotices } from '../../../../../redux/services/preliminaryNoticesService';
import { DillButton, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';
import { PreliminaryNoticeApiResponse, PrelienStatus } from '@dill/dill-shared';
import { Export, Eye } from '@phosphor-icons/react';
import { exportToCsv, exportToXlsx } from './exportUtils';

export type PrelimRow = {
  jobName: string;
  jobNumber: string;
  address: string;
  submittedDate: string;
  status: 'Pending' | 'Complete';
  // hidden job details
  customer: string;
  customerJobNumber: string;
  owner: string;
  gc: string;
  lender: string;
  county: string;
  jobType: string;
  jobSector: string;
  surety: string;
  apn: string;
  // hidden status details
  ownersStatus: string;
  contractorsStatus: string;
  lendersStatus: string;
  customersStatus: string;
  suretiesStatus: string;
};

// ─── Column helpers ────────────────────────────────────────────────────────────

const textColumn = (key: keyof PrelimRow, label: string, width = 200): DillColumn<PrelimRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  toggleable: true,
  render: (value: string) => <div className="text-xs text-gray-500">{value || '—'}</div>,
});

const getColumns = (): DillColumn<PrelimRow>[] => [
  textColumn('jobName', 'Job Name', 200),
  textColumn('jobNumber', 'Job Number', 130),
  textColumn('address', 'Address', 280),
  { ...textColumn('submittedDate', 'Submitted Date', 140), filterType: 'dateRange' },
  {
    key: 'status',
    label: 'Status',
    width: 110,
    sortable: true,
    filterable: true,
    filterType: 'values',
    toggleable: true,
    render: (value: string) => {
      const isPending = value === 'Pending';
      return (
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            isPending ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
          }`}>
          {value}
        </span>
      );
    },
  },
  // ─── Hidden job detail columns ────────────────────────────────────────────
  { ...textColumn('customer', 'Customer', 160), defaultHidden: true },
  { ...textColumn('customerJobNumber', 'Customer Job #', 140), defaultHidden: true },
  { ...textColumn('owner', 'Owner', 160), defaultHidden: true },
  { ...textColumn('gc', 'General Contractor', 180), defaultHidden: true },
  { ...textColumn('lender', 'Lender', 160), defaultHidden: true },
  { ...textColumn('county', 'County', 130), defaultHidden: true },
  {
    ...textColumn('jobType', 'Job Type', 120),
    defaultHidden: true,
    filterable: true,
    filterType: 'values',
  },
  {
    ...textColumn('jobSector', 'Job Sector', 120),
    defaultHidden: true,
    filterable: true,
    filterType: 'values',
  },
  { ...textColumn('surety', 'Surety', 160), defaultHidden: true },
  { ...textColumn('apn', 'APN', 140), defaultHidden: true },
  // ─── Hidden status detail columns ─────────────────────────────────────────
  { ...textColumn('ownersStatus', 'Owner Status', 260), defaultHidden: true },
  { ...textColumn('contractorsStatus', 'Contractor Status', 260), defaultHidden: true },
  { ...textColumn('lendersStatus', 'Lender Status', 260), defaultHidden: true },
  { ...textColumn('customersStatus', 'Customer Status', 260), defaultHidden: true },
  { ...textColumn('suretiesStatus', 'Surety Status', 260), defaultHidden: true },
];

// ─── Flattening helpers ───────────────────────────────────────────────────────

const formatStatusArray = (statuses: PrelienStatus[] | undefined | null): string => {
  if (!statuses?.length) return '';
  return statuses
    .filter((s) => s.isNeeded !== false)
    .map((s) => {
      const label = s.name || 'Unknown';
      const mailed = s.mailedDate ? `mailed ${moment(s.mailedDate).format('MM/DD/YYYY')}` : null;
      const delivered = s.deliveredDate
        ? `delivered ${moment(s.deliveredDate).format('MM/DD/YYYY')}`
        : null;
      const statusLabel = s.status ?? null;
      const details = [mailed, delivered, statusLabel].filter(Boolean).join(', ');
      return details ? `${label} (${details})` : label;
    })
    .join('; ');
};

// ─── Status derivation ────────────────────────────────────────────────────────

function deriveStatus(prelim: PreliminaryNoticeApiResponse): 'Pending' | 'Complete' {
  if (prelim.status === 'completed') return 'Complete';
  const allMailed =
    (prelim.lendersStatus?.length
      ? prelim.lendersStatus.filter((s) => s.isNeeded !== false).every((s) => s.mailedDate != null)
      : true) &&
    (prelim.ownersStatus?.length
      ? prelim.ownersStatus.filter((s) => s.isNeeded !== false).every((s) => s.mailedDate != null)
      : true) &&
    (prelim.contractorsStatus?.length
      ? prelim.contractorsStatus
          .filter((s) => s.isNeeded !== false)
          .every((s) => s.mailedDate != null)
      : true) &&
    (prelim.customersStatus?.length
      ? prelim.customersStatus
          .filter((s) => s.isNeeded !== false)
          .every((s) => s.mailedDate != null)
      : true) &&
    (prelim.suretiesStatus?.length
      ? prelim.suretiesStatus.filter((s) => s.isNeeded !== false).every((s) => s.mailedDate != null)
      : true);
  return allMailed ? 'Complete' : 'Pending';
}

// ─── Component ────────────────────────────────────────────────────────────────

function PreliminaryNoticesReporting() {
  const dispatch = useAppDispatch();
  const { currentSupplier } = useAppSelector((state) => state.auth);
  const { preliminaryNotices, loadingList } = useAppSelector((state) => state.preliminaryNotices);
  const isLoading = loadingList.includes('getSupplierPreliminaryNotices');

  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  // Rows after the table's search/filters/sorting are applied — what we export.
  const [filteredData, setFilteredData] = useState<PrelimRow[]>([]);
  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const allColumns = useMemo(() => getColumns(), []);

  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };
    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportOpen, isColumnsOpen]);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierPreliminaryNotices({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id]);

  const rows = useMemo((): PrelimRow[] => {
    return (preliminaryNotices ?? []).map((prelim) => {
      const job = prelim.job;
      const addressParts = [job?.jobAddress, job?.jobCity, job?.jobState, job?.jobZipCode].filter(
        Boolean
      );
      return {
        jobName: job?.name ?? '',
        jobNumber: job?.jobNumber ?? '',
        address: addressParts.join(', '),
        submittedDate: prelim.createdAt ? moment(prelim.createdAt).format('MM/DD/YYYY') : '',
        status: deriveStatus(prelim),
        customer: job?.buyer?.name ?? '',
        customerJobNumber: job?.customerJobNumber ?? '',
        owner: job?.owner ?? '',
        gc: job?.GC ?? '',
        lender: job?.lender ?? '',
        county: job?.jobCounty ?? '',
        jobType: job?.jobType ?? '',
        jobSector: job?.jobSector ?? '',
        surety: job?.surety ?? '',
        apn: job?.apn ?? '',
        ownersStatus: formatStatusArray(prelim.ownersStatus),
        contractorsStatus: formatStatusArray(prelim.contractorsStatus),
        lendersStatus: formatStatusArray(prelim.lendersStatus),
        customersStatus: formatStatusArray(prelim.customersStatus),
        suretiesStatus: formatStatusArray(prelim.suretiesStatus),
      };
    });
  }, [preliminaryNotices]);

  const exportColumns = useMemo(
    () =>
      visibleColumns.map((c) => ({
        key: c.key as keyof PrelimRow,
        label: typeof c.label === 'string' ? c.label : c.key,
      })),
    [visibleColumns]
  );

  const handleExportXlsx = () => {
    exportToXlsx(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    exportToCsv(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col mt-4">
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          Find all submitted preliminary notices below. Filter, sort, and resize columns as needed.
          Export to Excel or CSV.
        </p>
      </div>

      <DillTable<PrelimRow>
        columns={visibleColumns}
        data={rows}
        onProcessedDataChange={setFilteredData}
        rowKey={(r) => `${r.jobNumber}-${r.submittedDate}`}
        virtualize
        verticalDividers
        search
        searchPlaceholder="Search by job name or number…"
        pagination
        pageSize={25}
        rowHeight={35}
        scrollX
        showActiveFilters
        striped={false}
        maxHeight="calc(100vh - 280px)"
        showFooter={false}
        loading={isLoading}
        loadingText="Loading preliminary notices..."
        emptyMessage="No preliminary notices found"
        showCount={false}
        toolbarRight={
          <div className="flex gap-2">
            <div className="relative" ref={columnsRef}>
              <DillButton
                label="Columns"
                onClick={() => setIsColumnsOpen(!isColumnsOpen)}
                icon={<Eye size={14} />}
                variant="ghost"
              />
              {isColumnsOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
                    <span>Show/Hide Columns</span>
                    <button
                      onClick={() => setHiddenCols(new Set())}
                      className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
                      Select All
                    </button>
                  </div>
                  {allColumns.map((col) => {
                    const isVisible = !hiddenCols.has(col.key);
                    const canHide = isVisible && visibleColumns.length <= 3;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                          canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumn(col.key)}
                          disabled={canHide}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                        />
                        <span className="flex-1">{col.label}</span>
                        {canHide && (
                          <span className="text-[10px] text-gray-400">Min 3 required</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              className="relative"
              ref={exportRef}
              title={isLoading ? 'Still loading preliminary notices…' : undefined}>
              <DillButton
                label="Export"
                onClick={() => setIsExportOpen(!isExportOpen)}
                icon={<Export size={14} />}
                variant="primary"
                isDisabled={isLoading}
              />
              {isExportOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                  <button
                    onClick={handleExportXlsx}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as XLSX
                  </button>
                  <button
                    onClick={handleExportCsv}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
}

export default PreliminaryNoticesReporting;
